import { COMMUNICATIONS_NAMESPACE } from '@amitable/shared/realtime-namespaces'
import { useEffect, useMemo, useState } from 'react'
import { useApolloClient } from '@apollo/client/react'
import { io } from 'socket.io-client'
import { unbindCommunicationPush } from './device-push'
import { CommunicationNotificationContext as Context } from './notification-context'
import {
  createNotificationRefresh,
  loadNotificationFeed,
} from './notification-feed'
import { useDeviceReconciliation } from './use-device-reconciliation'
import type { NotificationFeed, Workspace } from './notification-feed'
import type { ReactNode } from 'react'
import { AcknowledgeCommunicationNotificationsDocument } from '@/graphql/queries/communicationNotificationsQuery.gql.generated'
import { useCurrentUserProfile } from '@/entities/user/model/use-current-user-profile'
import { resolvePublicApiUrl } from '@/shared/lib/api-url'
import { env } from '@/env'
import { appTitle } from '@/shared/config/app-config'
import { CUSTOMER_AUTH_SESSION_EXPIRED_EVENT } from '@/shared/lib/customer-auth'

type FeedState = NotificationFeed & { userId?: string; workspaceId?: string }
export function CommunicationNotificationsProvider({
  children,
}: {
  children: ReactNode
}) {
  const client = useApolloClient()
  const { currentUser } = useCurrentUserProfile()
  const workspaceId = currentUser?.workspaceId
  const userId = currentUser?.id
  const canRead = currentUser?.can('read', 'communication') ?? false
  const workspaces = useMemo(() => {
    if (currentUser?.availableWorkspaces.length)
      return currentUser.availableWorkspaces

    return currentUser?.workspace ? [currentUser.workspace] : []
  }, [currentUser])
  const [state, setState] = useState<FeedState>({ total: 0, entries: [] })
  useDeviceReconciliation(userId, workspaceId, canRead)
  useNotificationLifecycle({
    client,
    userId,
    workspaceId,
    canRead,
    workspaces,
    setState,
  })
  const total =
    state.userId === userId && state.workspaceId === workspaceId
      ? state.total
      : 0
  const entries = state.userId === userId ? state.entries : []
  useEffect(() => {
    document.title = total > 0 ? `(${total}) ${appTitle}` : appTitle

    return () => {
      document.title = appTitle
    }
  }, [total])
  const acknowledge = async () => {
    for (const workspace of workspaces) {
      const ids = entries
        .filter(
          (entry) =>
            entry.workspaceId === workspace.id && !entry.acknowledgedAt,
        )
        .map((entry) => entry.id)
      for (let index = 0; index < ids.length; index += 100)
        await client.mutate({
          mutation: AcknowledgeCommunicationNotificationsDocument,
          variables: {
            workspaceId: workspace.id,
            ids: ids.slice(index, index + 100),
          },
        })
    }
    setState((current) => ({
      ...current,
      entries: current.entries.map((entry) => ({
        ...entry,
        acknowledgedAt: new Date().toISOString(),
      })),
    }))
  }

  return (
    <Context.Provider value={{ total, entries, acknowledge }}>
      {children}
    </Context.Provider>
  )
}

type Lifecycle = {
  client: ReturnType<typeof useApolloClient>
  userId?: string
  workspaceId?: string | null
  canRead: boolean
  workspaces: Array<Workspace>
  setState: (state: FeedState) => void
}
function useNotificationLifecycle({
  client,
  userId,
  workspaceId,
  canRead,
  workspaces,
  setState,
}: Lifecycle) {
  useEffect(() => {
    if (!userId || !workspaceId) return
    const refresh = createNotificationRefresh(
      () => loadNotificationFeed(client, workspaces, workspaceId, canRead),
      (feed) => setState({ ...feed, userId, workspaceId }),
    )
    const invalidate = () => {
      void refresh.refresh()
      void client
        .refetchQueries({
          include: 'active',
          onQueryUpdated(query) {
            if (
              [
                'CommunicationInbox',
                'CommunicationConversation',
                'CommunicationConversationMessages',
              ].includes(query.queryName ?? '')
            )
              return query.refetch()

            return false
          },
        })
        .catch(() => {})
    }
    const socket = io(
      resolvePublicApiUrl(env.VITE_API_URL, COMMUNICATIONS_NAMESPACE),
      { autoConnect: false, transports: ['websocket'], withCredentials: true },
    )
    socket.on('connect', () => {
      for (const workspace of workspaces)
        socket.emit('communications.subscribe', { workspaceId: workspace.id })
      invalidate()
    })
    for (const event of [
      'communications.message.received',
      'communications.inbox.changed',
      'communications.notifications.changed',
      'workspace.access.updated',
      'workspace.access.revoked',
    ])
      socket.on(event, invalidate)
    const visible = () => {
      if (document.visibilityState === 'visible') invalidate()
    }
    const expired = () => {
      void unbindCommunicationPush().catch(() => {})
      socket.disconnect()
      setState({ total: 0, entries: [] })
    }
    socket.connect()
    document.addEventListener('visibilitychange', visible)
    window.addEventListener(CUSTOMER_AUTH_SESSION_EXPIRED_EVENT, expired)
    void refresh.refresh()

    return () => {
      refresh.dispose()
      socket.disconnect()
      document.removeEventListener('visibilitychange', visible)
      window.removeEventListener(CUSTOMER_AUTH_SESSION_EXPIRED_EVENT, expired)
    }
  }, [client, userId, workspaceId, canRead, workspaces, setState])
}
